import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { Eye, EyeOff, Pencil, Plus, Trash2 } from 'lucide-react';
import { adminApi } from '@/api';
import { DiscordRolePicker } from '@/components/admin/DiscordRolePicker';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { formatRub } from '@/lib/utils';
import { PRODUCT_KIND, ruLabel } from '@/lib/labels';

type ProductForm = {
  id?: number;
  slug: string;
  kind: 'pass' | 'role' | 'item' | 'bundle' | 'custom';
  categoryId: string;
  name: string;
  description: string;
  imageUrl: string;
  priceRub: number;
  oldPriceRub: number;
  serverId: string;
  durationDays: number;
  durations: Array<{ days: number; priceRub: number; label: string }>;
  discordRoleIds: string[];
  paperGroup: string;          // LuckPerms-группа
  paperItems: Array<{ material: string; count: number; displayName: string }>;
  maxPerUser: string;
  commands: string;
  expireCommands: string;
  isEnabled: boolean;
  sortOrder: number;
};

const emptyForm = (): ProductForm => ({
  slug: '',
  kind: 'custom',
  categoryId: '',
  name: '',
  description: '',
  imageUrl: '',
  priceRub: 0,
  oldPriceRub: 0,
  serverId: '',
  durationDays: 0,
  durations: [],
  discordRoleIds: [],
  paperGroup: '',
  paperItems: [],
  maxPerUser: '',
  commands: '',
  expireCommands: '',
  isEnabled: true,
  sortOrder: 0,
});

function toPayload(f: ProductForm) {
  return {
    slug: f.slug,
    kind: f.kind,
    categoryId: f.categoryId ? Number(f.categoryId) : null,
    name: f.name,
    description: f.description || null,
    imageUrl: f.imageUrl || null,
    priceMinor: Math.round(f.priceRub * 100),
    oldPriceMinor: f.oldPriceRub > 0 ? Math.round(f.oldPriceRub * 100) : null,
    serverId: f.serverId ? Number(f.serverId) : null,
    durationSec: f.durationDays > 0 ? f.durationDays * 86_400 : null,
    durations: f.durations
      .filter((d) => d.priceRub >= 0)
      .map((d) => ({
        days: d.days,
        priceMinor: Math.round(d.priceRub * 100),
        ...(d.label.trim() ? { label: d.label.trim() } : {}),
      })),
    discordRoleIds: [...new Set(
      f.discordRoleIds
        .map((id) => id.trim())
        .filter((id) => /^\d{17,20}$/.test(id)),
    )],
    paperGroup: f.paperGroup.trim() || null,
    paperItems: f.paperItems
      .filter((i) => i.material.trim().length > 0 && i.count > 0)
      .map((i) => ({
        material: i.material.trim(),
        count: i.count,
        ...(i.displayName.trim() ? { displayName: i.displayName.trim() } : {}),
      })),
    maxPerUser: f.maxPerUser ? Number(f.maxPerUser) : null,
    commands: f.commands.split('\n').map((s) => s.trim()).filter(Boolean),
    expireCommands: f.expireCommands.split('\n').map((s) => s.trim()).filter(Boolean),
    isEnabled: f.isEnabled,
    sortOrder: f.sortOrder,
  };
}

export function AdminProducts() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin', 'products'], queryFn: adminApi.products.list });
  const { data: categories } = useQuery({
    queryKey: ['admin', 'categories'],
    queryFn: adminApi.categories.list,
  });
  const rolesQuery = useQuery({
    queryKey: ['admin', 'discord-roles'],
    queryFn: adminApi.discordSettings.roles,
    retry: false,
  });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<ProductForm>(emptyForm());

  const createMut = useMutation({
    mutationFn: () => adminApi.products.create(toPayload(form)),
    onSuccess: () => {
      toast.success('Создано');
      qc.invalidateQueries({ queryKey: ['admin', 'products'] });
      setOpen(false);
    },
    onError: () => toast.error('Не удалось сохранить'),
  });

  const updateMut = useMutation({
    mutationFn: () => adminApi.products.update(form.id!, toPayload(form)),
    onSuccess: () => {
      toast.success('Сохранено');
      qc.invalidateQueries({ queryKey: ['admin', 'products'] });
      setOpen(false);
    },
    onError: () => toast.error('Не удалось сохранить'),
  });

  const toggleMut = useMutation({
    mutationFn: (id: number) => adminApi.products.toggle(id),
    onSuccess: (r) => {
      toast.success(r.isEnabled ? 'Включён' : 'Отключён');
      qc.invalidateQueries({ queryKey: ['admin', 'products'] });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
    onError: () => toast.error('Не удалось переключить'),
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => adminApi.products.remove(id),
    onSuccess: () => {
      toast.success('Товар удалён');
      qc.invalidateQueries({ queryKey: ['admin', 'products'] });
      qc.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (err: AxiosError<{ error?: string; orderCount?: number }>) => {
      const code = err.response?.data?.error;
      if (code === 'product_has_orders') {
        toast.error('У товара уже есть покупки, его можно только отключить');
      } else {
        toast.error('Не удалось удалить товар');
      }
    },
  });

  function openCreate() {
    setForm(emptyForm());
    setOpen(true);
  }

  // Защищённый парсер: JSON-колонка может прийти как массив или как строка.
  function asLines(v: unknown): string {
    if (Array.isArray(v)) return v.join('\n');
    if (typeof v === 'string') {
      try {
        const parsed = JSON.parse(v);
        if (Array.isArray(parsed)) return parsed.join('\n');
      } catch {
        return v;
      }
    }
    return '';
  }

  function asArray(v: unknown): unknown[] {
    if (Array.isArray(v)) return v;
    if (typeof v === 'string') {
      try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; }
    }
    return [];
  }
  function parseDurations(v: unknown): ProductForm['durations'] {
    return asArray(v).map((d: any) => ({
      days: Number(d?.days ?? 0),
      priceRub: Number(d?.priceMinor ?? 0) / 100,
      label: typeof d?.label === 'string' ? d.label : '',
    }));
  }
  function parseItems(v: unknown): ProductForm['paperItems'] {
    return asArray(v).map((i: any) => ({
      material: typeof i?.material === 'string' ? i.material : '',
      count: Number(i?.count ?? 1) || 1,
      displayName: typeof i?.displayName === 'string' ? i.displayName : '',
    }));
  }
  function parseRoles(v: unknown): string[] {
    let values: unknown[] = [];
    if (Array.isArray(v)) {
      values = v;
    } else if (typeof v === 'string') {
      try {
        const parsed = JSON.parse(v);
        values = Array.isArray(parsed) ? parsed : v.split(/[\s,]+/);
      } catch {
        values = v.split(/[\s,]+/);
      }
    }
    return [...new Set(
      values
        .filter((id): id is string => typeof id === 'string')
        .map((id) => id.trim())
        .filter((id) => /^\d{17,20}$/.test(id)),
    )];
  }

  function openEdit(p: any) {
    try {
      setForm({
        id: p.id,
        slug: p.slug ?? '',
        kind: p.kind ?? 'custom',
        categoryId: p.categoryId ? String(p.categoryId) : '',
        name: p.name ?? '',
        description: p.description ?? '',
        imageUrl: p.imageUrl ?? '',
        priceRub: Number(p.priceMinor ?? 0) / 100,
        oldPriceRub: Number(p.oldPriceMinor ?? 0) / 100,
        serverId: p.serverId ? String(p.serverId) : '',
        durationDays: p.durationSec ? Math.round(Number(p.durationSec) / 86_400) : 0,
        durations: parseDurations(p.durations),
        discordRoleIds: parseRoles(p.discordRoleIds),
        paperGroup: typeof p.paperGroup === 'string' ? p.paperGroup : '',
        paperItems: parseItems(p.paperItems),
        maxPerUser: p.maxPerUser ? String(p.maxPerUser) : '',
        commands: asLines(p.commands),
        expireCommands: asLines(p.expireCommands),
        isEnabled: Boolean(p.isEnabled),
        sortOrder: Number(p.sortOrder ?? 0),
      });
      setOpen(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('openEdit failed', err, p);
      toast.error('Не удалось открыть редактор - см. консоль');
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-extrabold text-eol-white">Товары</h1>
        <Button onClick={openCreate} className="gap-2">
          <Plus className="h-4 w-4" /> Создать
        </Button>
      </div>

      <div className="space-y-3">
        {(data as any[])?.map((p) => (
          <Card key={p.id}>
            <CardContent className="pt-4 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-eol-white">{p.name}</h3>
                  <Badge variant="secondary">{ruLabel(PRODUCT_KIND, p.kind)}</Badge>
                  {!p.isEnabled && <Badge variant="danger">Выключен</Badge>}
                </div>
                <p className="text-xs text-eol-desc mt-1">Код: {p.slug}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-eol-pink font-bold">{formatRub(p.priceMinor)}</span>
                <Button
                  size="icon"
                  variant="secondary"
                  onClick={() => openEdit(p)}
                  title="Редактировать"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => toggleMut.mutate(p.id)}
                  title={p.isEnabled ? 'Отключить' : 'Включить'}
                  disabled={toggleMut.isPending}
                >
                  {p.isEnabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4 opacity-50" />}
                </Button>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => {
                    if (confirm(`Удалить «${p.name}»? Это действие нельзя отменить.`)) {
                      removeMut.mutate(p.id);
                    }
                  }}
                  title="Удалить"
                  disabled={removeMut.isPending}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{form.id ? 'Редактировать товар' : 'Новый товар'}</DialogTitle>
            <DialogDescription>
              Команды поддерживают переменные: <code>{'{nickname}'}</code>, <code>{'{server}'}</code>, <code>{'{orderId}'}</code>.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Название">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Код (латиница, для ссылок)">
              <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="vip-1m" />
            </Field>
            <Field label="Тип">
              <Select
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value as ProductForm['kind'] })}
              >
                <option value="pass">Проходка</option>
                <option value="role">Роль</option>
                <option value="item">Предмет</option>
                <option value="bundle">Набор</option>
                <option value="custom">Прочее</option>
              </Select>
            </Field>
            <Field label="Категория (витрина)">
              <Select
                value={form.categoryId}
                onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
              >
                <option value="">- без категории -</option>
                {categories?.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="ID сервера (пусто = все серверы)">
              <Input value={form.serverId} onChange={(e) => setForm({ ...form, serverId: e.target.value })} />
            </Field>
            <Field label="Цена, ₽">
              <Input type="number" value={form.priceRub} onChange={(e) => setForm({ ...form, priceRub: Number(e.target.value) })} />
            </Field>
            <Field label="Старая цена, ₽ (0 = нет)">
              <Input type="number" value={form.oldPriceRub} onChange={(e) => setForm({ ...form, oldPriceRub: Number(e.target.value) })} />
            </Field>
            <Field label="Длительность, дней (0 = вечно, игнорируется если есть тарифы ниже)">
              <Input type="number" value={form.durationDays} onChange={(e) => setForm({ ...form, durationDays: Number(e.target.value) })} />
            </Field>
            <Field label="Лимит на пользователя">
              <Input value={form.maxPerUser} onChange={(e) => setForm({ ...form, maxPerUser: e.target.value })} placeholder="пусто = без лимита" />
            </Field>

            <Field label="Тарифы (опционально)" className="col-span-2">
              <div className="space-y-3">
                <p className="text-xs text-eol-desc">
                  Если задать тарифы - при покупке откроется выбор срока. Если оставить пусто - используется цена и длительность выше.
                  <strong className="text-eol-white"> 0 дней</strong> = «навсегда».
                </p>

                {form.durations.length > 0 && (
                  <div className="grid grid-cols-[110px_140px_1fr_40px] gap-2 text-[10px] uppercase font-bold tracking-wider text-eol-desc px-1">
                    <span>Срок, дней</span>
                    <span>Цена, ₽</span>
                    <span>Метка (опц.)</span>
                    <span />
                  </div>
                )}

                {form.durations.map((d, i) => (
                  <div key={i} className="grid grid-cols-[110px_140px_1fr_40px] gap-2 items-center">
                    <Input
                      type="number"
                      min={0}
                      value={d.days}
                      onChange={(e) => {
                        const next = [...form.durations];
                        next[i] = { ...next[i]!, days: Number(e.target.value) };
                        setForm({ ...form, durations: next });
                      }}
                    />
                    <Input
                      type="number"
                      min={0}
                      value={d.priceRub}
                      onChange={(e) => {
                        const next = [...form.durations];
                        next[i] = { ...next[i]!, priceRub: Number(e.target.value) };
                        setForm({ ...form, durations: next });
                      }}
                    />
                    <Input
                      placeholder="напр. «3 месяца» или оставьте пусто"
                      value={d.label}
                      onChange={(e) => {
                        const next = [...form.durations];
                        next[i] = { ...next[i]!, label: e.target.value };
                        setForm({ ...form, durations: next });
                      }}
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      onClick={() =>
                        setForm({ ...form, durations: form.durations.filter((_, j) => j !== i) })
                      }
                      title="Удалить тариф"
                    >
                      ✕
                    </Button>
                  </div>
                ))}

                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    setForm({
                      ...form,
                      durations: [...form.durations, { days: 30, priceRub: 0, label: '' }],
                    })
                  }
                >
                  + Добавить тариф
                </Button>
              </div>
            </Field>
            <Field label="URL картинки" className="col-span-2">
              <Input value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} />
            </Field>
            <Field label="Описание" className="col-span-2">
              <textarea
                rows={3}
                className="w-full rounded-md border border-white/10 bg-eol-black/40 px-3 py-2 text-sm text-eol-white"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Field>
            <div className="col-span-2 space-y-1.5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-eol-desc">
                  Discord-роли после покупки
                </p>
                <p className="mt-1 text-xs text-eol-desc">
                  Выдаются независимо от типа товара. Для временного товара роли снимаются после окончания.
                </p>
              </div>
              <DiscordRolePicker
                value={form.discordRoleIds}
                onChange={(discordRoleIds) => setForm({ ...form, discordRoleIds })}
                roles={rolesQuery.data}
                isLoading={rolesQuery.isLoading}
                isError={rolesQuery.isError}
                disabled={createMut.isPending || updateMut.isPending}
              />
              <p className="text-xs text-eol-desc">
                Для выдачи бот должен иметь право Manage Roles, а его роль должна находиться выше выбранных.
              </p>
            </div>

            <Field label="Предметы (для корзины Paper-плагина)" className="col-span-2">
              <div className="space-y-2">
                {form.paperItems.length > 0 && (
                  <div className="grid grid-cols-[1fr_90px_1fr_40px] gap-2 text-[10px] uppercase font-bold tracking-wider text-eol-desc px-1">
                    <span>Material (например, minecraft:diamond)</span>
                    <span>Кол-во</span>
                    <span>Имя предмета (опц.)</span>
                    <span />
                  </div>
                )}
                {form.paperItems.map((it, i) => (
                  <div key={i} className="grid grid-cols-[1fr_90px_1fr_40px] gap-2 items-center">
                    <Input
                      value={it.material}
                      onChange={(e) => {
                        const next = [...form.paperItems];
                        next[i] = { ...next[i]!, material: e.target.value };
                        setForm({ ...form, paperItems: next });
                      }}
                      placeholder="minecraft:diamond"
                      className="font-mono text-xs"
                    />
                    <Input
                      type="number"
                      min={1}
                      max={64}
                      value={it.count}
                      onChange={(e) => {
                        const next = [...form.paperItems];
                        next[i] = { ...next[i]!, count: Number(e.target.value) };
                        setForm({ ...form, paperItems: next });
                      }}
                    />
                    <Input
                      value={it.displayName}
                      onChange={(e) => {
                        const next = [...form.paperItems];
                        next[i] = { ...next[i]!, displayName: e.target.value };
                        setForm({ ...form, paperItems: next });
                      }}
                      placeholder="Алмаз счастья"
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      onClick={() =>
                        setForm({ ...form, paperItems: form.paperItems.filter((_, j) => j !== i) })
                      }
                    >
                      ✕
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    setForm({
                      ...form,
                      paperItems: [...form.paperItems, { material: 'minecraft:diamond', count: 1, displayName: '' }],
                    })
                  }
                >
                  + Добавить предмет
                </Button>
              </div>
            </Field>

            <Field label="Команды на выдачу (по одной на строку)" className="col-span-2">
              <textarea
                rows={4}
                className="w-full rounded-md border border-white/10 bg-eol-black/40 px-3 py-2 text-sm font-mono text-eol-white"
                value={form.commands}
                onChange={(e) => setForm({ ...form, commands: e.target.value })}
                placeholder="lp user {nickname} parent add vip&#10;give {nickname} diamond 16"
              />
            </Field>
            <Field label="Команды на снятие (для временных)" className="col-span-2">
              <textarea
                rows={3}
                className="w-full rounded-md border border-white/10 bg-eol-black/40 px-3 py-2 text-sm font-mono text-eol-white"
                value={form.expireCommands}
                onChange={(e) => setForm({ ...form, expireCommands: e.target.value })}
                placeholder="lp user {nickname} parent remove vip"
              />
            </Field>
            <Field label="Порядок сортировки">
              <Input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })} />
            </Field>
            <Field label="Статус">
              <label className="flex items-center gap-2 h-10 text-eol-white">
                <input
                  type="checkbox"
                  checked={form.isEnabled}
                  onChange={(e) => setForm({ ...form, isEnabled: e.target.checked })}
                />
                Активен
              </label>
            </Field>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Отмена</Button>
            <Button onClick={() => (form.id ? updateMut.mutate() : createMut.mutate())}>
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={`flex flex-col gap-1.5 justify-end ${className ?? ''}`}>
      <span className="text-xs uppercase font-semibold tracking-wider text-eol-desc">{label}</span>
      {children}
    </label>
  );
}
